import { createPortal } from 'react-dom'
import { useEffect, useMemo, useState } from 'react'
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded'
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded'
import ViewStreamRoundedIcon from '@mui/icons-material/ViewStreamRounded'
import { motion } from 'framer-motion'
import { useDashboardViewControlsSlot } from '../../context/dashboardViewControlsSlotCore'
import { useClientPreferences } from '../../context/useClientPreferences'
import { usePersistedState } from '../../hooks/usePersistedState'
import type { DashboardViewProps } from '../../types/dashboardView'
import { CanvasOverlay } from '../Canvas/CanvasOverlay'
import { EmptyHubState } from '../Common/EmptyHubState'
import { Pressable } from '../Common/Pressable'
import { SandfallColumn } from './sandfall/SandfallColumn'
import { SandfallScrubber } from './sandfall/SandfallScrubber'
import {
  collapseSandfallColumns,
  type SandColumn,
  type SandfallMode,
  useSandfallModel,
} from './sandfall/useSandfallModel'
import {
  useSandfallHistory,
  type SandfallColumnSnapshot,
  type SandfallWindowDays,
} from './sandfall/useSandfallHistory'

const applySnapshotsToColumns = (
  columns: readonly SandColumn[],
  snapshots: readonly SandfallColumnSnapshot[] | undefined,
): SandColumn[] => {
  if (!snapshots?.length) return [...columns]
  const snapshotByGoalId = new Map(snapshots.map((snapshot) => [snapshot.goalId, snapshot]))
  return columns.map((column) => {
    const snapshot = snapshotByGoalId.get(column.goalId)
    if (!snapshot) return column
    const ratio = snapshot.totalWeight > 0
      ? Math.max(0, Math.min(1, snapshot.remainingWeight / snapshot.totalWeight))
      : 0
    return {
      ...column,
      remainingWeight: snapshot.remainingWeight,
      bands: column.bands.map((band) => ({
        ...band,
        remaining: band.total * ratio,
      })),
    }
  })
}

const SandfallSlotControls = ({
  mode,
  reducedMotion,
  inlineFallback = false,
  onModeChange,
}: {
  mode: SandfallMode
  reducedMotion: boolean
  inlineFallback?: boolean
  onModeChange: (mode: SandfallMode) => void
}) => (
  <div className={`sandfallSlotControls${inlineFallback ? ' sandfallSlotControls--inline' : ''}`}>
    <div className="sandfallModeToggle" role="tablist" aria-label="Sandfall mode">
      {(['unified', 'flat'] as const).map((option) => {
        const active = mode === option
        return (
          <Pressable
            key={option}
            role="tab"
            aria-selected={active}
            className={`sandfallModeButton${active ? ' isActive' : ''}`}
            onClick={() => onModeChange(option)}
          >
            {active && (
              <motion.span
                layoutId="sandfallModeIndicator"
                className="sandfallModeIndicator"
                transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 32 }}
              />
            )}
            <span className="sandfallModeButtonLabel">
              {option === 'unified' ? <ViewStreamRoundedIcon fontSize="inherit" /> : <ViewColumnRoundedIcon fontSize="inherit" />}
              {option === 'unified' ? 'Unified' : 'Flat'}
            </span>
          </Pressable>
        )
      })}
    </div>
  </div>
)

export const SandfallView = ({
  elements,
  visualItems,
  onElementClick,
}: DashboardViewProps) => {
  const { preferences } = useClientPreferences()
  const reducedMotion = preferences.animationProfile === 'REDUCED_MOTION'
  const controlsSlotEl = useDashboardViewControlsSlot()
  const [mode, setMode] = useState<SandfallMode>('unified')
  const [windowDays, setWindowDays] = usePersistedState<SandfallWindowDays>('dashboard.sandfall.window', 30)
  const model = useSandfallModel(elements, visualItems)
  const history = useSandfallHistory(model.columns, visualItems, elements, windowDays)
  const [historyIndex, setHistoryIndex] = useState(0)
  const elementById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])

  useEffect(() => {
    setHistoryIndex(Math.max(0, history.points.length - 1))
  }, [history.points.length, windowDays])

  const isScrubbing = history.hasActivity && history.points.length > 0 && historyIndex < history.points.length - 1
  const snapshotColumns = history.points[historyIndex]?.columns
  const historicalColumns = useMemo(
    () => applySnapshotsToColumns(model.columns, isScrubbing ? snapshotColumns : undefined),
    [isScrubbing, model.columns, snapshotColumns],
  )
  const displayColumns = useMemo(
    () => mode === 'unified' ? collapseSandfallColumns(historicalColumns) : historicalColumns,
    [historicalColumns, mode],
  )
  const drainedPercent = model.totalWeight > 0
    ? Math.round((model.drainedWeight / model.totalWeight) * 100)
    : 0

  if (model.columns.length === 0) {
    return (
      <section className="sandfallView">
        <EmptyHubState
          className="sandfallEmpty"
          icon={<HourglassBottomRoundedIcon fontSize="inherit" />}
          title="Add a goal to start the sandfall"
          description="Remaining work will appear as weighted sand columns once the dashboard has goals."
          compact
        />
      </section>
    )
  }

  return (
    <section className="sandfallView sandfallView--withOverlayChrome" aria-label="Burndown Sandfall view">
      {controlsSlotEl
        ? createPortal(
            <SandfallSlotControls mode={mode} reducedMotion={reducedMotion} onModeChange={setMode} />,
            controlsSlotEl,
          )
        : (
          <SandfallSlotControls mode={mode} reducedMotion={reducedMotion} onModeChange={setMode} inlineFallback />
        )}

      <CanvasOverlay anchor="viewport" className="sandfallView__summaryOverlay">
        <div className="sandfallView__summary">
          <span>
            <HourglassBottomRoundedIcon fontSize="inherit" />
            {Math.round(model.totalWeight - model.drainedWeight)} weight left
          </span>
          <strong>{drainedPercent}% drained</strong>
        </div>
      </CanvasOverlay>

      <div className={`sandfallColumns sandfallColumns--${mode}`} data-dashboard-horizontal-scroll="true">
        {displayColumns.map((column) => (
          <SandfallColumn
            key={column.goalId}
            column={column}
            reducedMotion={reducedMotion}
            animateDrain={!isScrubbing}
            onSelect={mode === 'flat' ? () => {
              const element = elementById.get(column.goalId)
              if (element) onElementClick(element)
            } : undefined}
          />
        ))}
      </div>

      <CanvasOverlay anchor="viewport" className="sandfallScrubberOverlay pannableCanvasOverlayInteractive">
        <SandfallScrubber
          points={history.points}
          selectedIndex={historyIndex}
          windowDays={windowDays}
          isLoading={history.isLoading}
          hasActivity={history.hasActivity}
          onSelectedIndexChange={setHistoryIndex}
          onWindowDaysChange={setWindowDays}
        />
      </CanvasOverlay>
    </section>
  )
}
